package com.partejar.app.feature.convite.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.core.session.SessionCoordinator
import com.partejar.app.core.session.SessionDestination
import com.partejar.app.core.session.SessionTokens
import com.partejar.app.feature.auth.data.repository.AuthRepository
import com.partejar.app.feature.convite.data.ConviteAceiteDataSource
import com.partejar.app.feature.convite.data.ConviteAceiteDetails
import com.partejar.app.feature.convite.data.ConviteAceiteInput
import com.partejar.app.feature.convite.data.ConviteAcessoException
import com.partejar.app.feature.convite.data.ConviteInvalidoException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

sealed interface ConviteAceiteUiState {
    data object Loading : ConviteAceiteUiState
    data class Content(val details: ConviteAceiteDetails) : ConviteAceiteUiState
    data class Error(val message: String) : ConviteAceiteUiState
}

sealed interface ConviteAceiteActionState {
    data object Idle : ConviteAceiteActionState
    data object Submitting : ConviteAceiteActionState
    data class Error(val message: String) : ConviteAceiteActionState
    data class Success(val destination: SessionDestination) : ConviteAceiteActionState
}

class ConviteAceiteViewModel(
    private val dataSource: ConviteAceiteDataSource,
    private val authRepository: AuthRepository,
    private val sessionCoordinator: SessionCoordinator,
    private val coroutineScope: CoroutineScope? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow<ConviteAceiteUiState>(ConviteAceiteUiState.Loading)
    val uiState: StateFlow<ConviteAceiteUiState> = _uiState.asStateFlow()
    private val _actionState = MutableStateFlow<ConviteAceiteActionState>(ConviteAceiteActionState.Idle)
    val actionState: StateFlow<ConviteAceiteActionState> = _actionState.asStateFlow()

    fun load(token: String) {
        _uiState.value = ConviteAceiteUiState.Loading
        (coroutineScope ?: viewModelScope).launch {
            _uiState.value = try {
                ConviteAceiteUiState.Content(dataSource.validate(token))
            } catch (_: ConviteInvalidoException) {
                ConviteAceiteUiState.Error("Este convite não está mais disponível.")
            } catch (_: Throwable) {
                ConviteAceiteUiState.Error("Não foi possível validar o convite. Tente novamente.")
            }
        }
    }

    fun acceptNewUser(token: String, name: String, password: String, confirmation: String) {
        if (name.isBlank() || password.length < 8 || password != confirmation) {
            _actionState.value = ConviteAceiteActionState.Error("Revise nome, senha e confirmação.")
            return
        }
        accept(token, ConviteAceiteInput(name, password))
    }

    fun acceptExistingUser(token: String) = accept(token, null)

    fun hasSession(): Boolean = authRepository.hasRecoverableSession()

    fun clearAction() { _actionState.value = ConviteAceiteActionState.Idle }

    private fun accept(token: String, input: ConviteAceiteInput?) {
        _actionState.value = ConviteAceiteActionState.Submitting
        (coroutineScope ?: viewModelScope).launch {
            _actionState.value = try {
                val result = dataSource.accept(token, input)
                authRepository.storeSession(SessionTokens(result.accessToken, result.refreshToken))
                ConviteAceiteActionState.Success(sessionCoordinator.resolveAndSelect(result.workspaceId))
            } catch (_: ConviteAcessoException) {
                ConviteAceiteActionState.Error("Entre com a conta convidada para continuar.")
            } catch (_: ConviteInvalidoException) {
                ConviteAceiteActionState.Error("Este convite não está mais disponível.")
            } catch (_: Throwable) {
                ConviteAceiteActionState.Error("Não foi possível aceitar o convite. Tente novamente.")
            }
        }
    }
}
